import {
  CalendarIcon,
  CopyIcon,
  PackageIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import BackButton from '@/components/blocks/shared/back-button';
import MetricsDisplay from '@/components/blocks/shared/metrics-display';
import {
  ApproveGuard,
  DeleteGuard,
  EditGuard,
  ViewGuard,
} from '@/components/guards/permission-guards';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import Container from '@/components/ui/container';
import { DataTable } from '@/components/ui/data-table';
import { NoInventoryPermission } from '@/components/ui/permission-fallback';
import Row from '@/components/ui/row';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { showToast } from '@/components/ui/toast';
import { useGlobalState } from '@/contexts/GlobalStateContext';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import { DataTableColumn } from '@/types/datatable';
import { InventoryItem } from '@/types/inventory';
import { getStatusColor, getStatusTextColor } from '@/utils/colors';
import {
  capitalizeFirstLetter,
  formatCurrency,
  formatDate,
} from '@/utils/formatting';
import { RESOURCES } from '@/utils/permissions';

// Loading skeleton components
const SkeletonMetric = () => (
  <Card className='p-6'>
    <div className='animate-pulse'>
      <div className='h-4 bg-gray-20 rounded w-1/2 mb-2'></div>
      <div className='h-8 bg-gray-20 rounded w-3/4'></div>
    </div>
  </Card>
);

const SkeletonDetailItem = () => (
  <div className='flex items-start gap-3'>
    <div className='bg-gray-20 p-2 rounded-full mt-1 animate-pulse'>
      <div className='w-4 h-4 bg-gray-30 rounded'></div>
    </div>
    <div className='flex-1 min-w-0'>
      <div className='animate-pulse'>
        <div className='h-3 bg-gray-20 rounded w-1/3 mb-2'></div>
        <div className='h-4 bg-gray-20 rounded w-2/3'></div>
      </div>
    </div>
  </div>
);

const SkeletonTable = () => (
  <div className='space-y-4'>
    <div className='flex justify-between items-center'>
      <div className='animate-pulse'>
        <div className='h-6 bg-gray-20 rounded w-32'></div>
      </div>
      <div className='animate-pulse'>
        <div className='h-10 bg-gray-20 rounded w-64'></div>
      </div>
    </div>
    <div className='border rounded-lg'>
      <div className='p-4 border-b bg-gray-5'>
        <div className='grid grid-cols-4 gap-4'>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className='animate-pulse'>
              <div className='h-4 bg-gray-20 rounded'></div>
            </div>
          ))}
        </div>
      </div>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className='p-4 border-b last:border-b-0'>
          <div className='grid grid-cols-4 gap-4'>
            {Array.from({ length: 4 }).map((_, j) => (
              <div key={j} className='animate-pulse'>
                <div className='h-4 bg-gray-20 rounded'></div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const InventoryShow: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const api = useEnhancedApi();
  const { showSheet } = useGlobalState();

  // State management
  const [inventory, setInventory] = useState<InventoryItem | null>(null);
  const [variants, setVariants] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<
    'details' | 'pricing' | 'variants'
  >('details');
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const fetchedOnceRef = useRef<string | null>(null);

  // Fetch inventory data
  useEffect(() => {
    const fetchInventoryData = async () => {
      if (!id) return;

      // Prevent duplicate initial calls (e.g., React StrictMode)
      if (fetchedOnceRef.current === id) return;
      fetchedOnceRef.current = id;

      setIsLoading(true);
      try {
        const inventoryResponse = await api.inventory.getById(id);

        if (inventoryResponse.success) {
          const payload = inventoryResponse.data as any;
          const inv = payload?.inventory ?? payload ?? null;
          setInventory(inv);
          // Normalize variants from inventory payload
          const normalized = Array.isArray(inv?.variant)
            ? inv.variant.map((v: any) => ({
                name: v.name,
                sellingPrice: v?.pricing?.sellingPrice ?? v.price ?? 0,
                costPrice:
                  typeof v?.pricing?.costPrice === 'number'
                    ? v.pricing.costPrice
                    : undefined,
                wholesalePrice:
                  typeof v?.pricing?.wholesalePrice === 'number'
                    ? v.pricing.wholesalePrice
                    : undefined,
                quantity: v.quantity ?? 0,
                reorderLevel: v.reorderLevel ?? 0,
              }))
            : [];
          setVariants(normalized);
        }
      } catch (error) {
        showToast(`Error loading inventory data ${error}`, false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInventoryData();
    // Intentionally only depend on id to avoid re-fetch loops from changing api identity
  }, [id]);

  // Handle inventory actions
  const handleInventoryAction = async (
    actionType: 'approve' | 'delete' | 'decline'
  ) => {
    if (!id) return;

    setActionLoading(actionType);
    try {
      let response;
      switch (actionType) {
        case 'approve':
          response = await api.inventory.approve(id);
          break;
        case 'decline':
          response = await api.inventory.reject(id);
          break;
        case 'delete':
          response = await api.inventory.delete(id);
          break;
      }

      if (response?.success) {
        showToast(response.message, true);
        if (actionType === 'delete') {
          navigate('/inventories');
        } else {
          window.location.reload();
        }
      } else {
        showToast(response?.message || 'Action failed', false);
      }
    } catch (error) {
      showToast(`Error performing action ${error}`, false);
    } finally {
      setActionLoading(null);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showToast('Copied to clipboard', true);
  };

  // Open inventory update sheet
  const openInventoryUpdateSheet = () => {
    showSheet({
      id: 'inventory-update',
      type: 'inventory-update',
      title: 'Update Product',
      data: {
        product: inventory,
        onProductUpdated: async () => {
          // Refetch inventory data after update
          if (id) {
            const response = await api.inventory.getById(id);
            if (response.success) {
              const payload = response.data;
              setInventory((payload as any)?.inventory ?? payload ?? null);
            }
          }
        },
      },
    });
  };

  // Metrics data
  const metricsData = useMemo(() => {
    if (!inventory) return [];

    const vars = (inventory as any)?.variant || [];
    const isVar = Boolean((inventory as any)?.isVariant);
    const totalQuantity = isVar
      ? vars.reduce((sum: number, v: any) => sum + (Number(v.quantity) || 0), 0)
      : Number((inventory as any)?.quantity) || 0;

    const available = (inventory as any)?.availableQuantity ?? totalQuantity;
    const m = (inventory as any)?.metrics || {};

    const cards = [
      {
        id: 'available-quantity',
        title: 'Available Stock',
        value: available || 0,
        color: 'brand' as const,
      },
      {
        id: 'total-sales',
        title: 'Total Sales',
        value: m.totalSales ?? 0,
        color: 'orange' as const,
      },
      {
        id: 'total-revenue',
        title: 'Total Revenue',
        value: formatCurrency(m.totalRevenue || 0),
        color: 'success' as const,
      },
      {
        id: 'avg-sale-price',
        title: 'Avg Sale Price',
        value: formatCurrency(m.averageSalePrice || 0),
        color: 'brand' as const,
      },
      {
        id: 'turnover-rate',
        title: 'Turnover Rate',
        value: `${Number(m.turnoverRate || 0).toFixed(1)}x`,
        color: 'warning' as const,
      },
      {
        id: 'days-on-hand',
        title: 'Days on Hand',
        value: m.daysOnHand ?? 0,
        color: 'pink' as const,
      },
    ];

    return cards;
  }, [inventory]);

  // Removed Top Customers feature per updated API usage

  // Variant table columns
  const variantColumns: DataTableColumn<any>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Variant Name',
        accessor: 'name',
        cell: ({ row }) => (
          <span className='font-medium'>{row.original.name}</span>
        ),
        sortable: true,
      },
      {
        id: 'sellingPrice',
        header: 'Selling Price',
        accessor: 'sellingPrice',
        cell: ({ row }) => (
          <span className='font-medium'>
            {formatCurrency(row.original.sellingPrice)}
          </span>
        ),
        sortable: true,
      },
      {
        id: 'costPrice',
        header: 'Cost Price',
        accessor: 'costPrice',
        cell: ({ row }) => (
          <span className='text-gray-80'>
            {typeof row.original.costPrice === 'number'
              ? formatCurrency(row.original.costPrice)
              : '—'}
          </span>
        ),
        sortable: true,
      },
      {
        id: 'wholesalePrice',
        header: 'Wholesale Price',
        accessor: 'wholesalePrice',
        cell: ({ row }) => (
          <span className='text-gray-80'>
            {typeof row.original.wholesalePrice === 'number'
              ? formatCurrency(row.original.wholesalePrice)
              : '—'}
          </span>
        ),
        sortable: true,
      },
      {
        id: 'quantity',
        header: 'Stock',
        accessor: 'quantity',
        cell: ({ row }) => (
          <span className='font-medium'>{row.original.quantity}</span>
        ),
        sortable: true,
      },
      {
        id: 'reorderLevel',
        header: 'Reorder Level',
        accessor: 'reorderLevel',
        cell: ({ row }) => (
          <span className='text-gray-60'>{row.original.reorderLevel}</span>
        ),
        sortable: true,
      },
    ],
    []
  );

  // Removed Top Customers feature per updated API usage

  // Show loading state
  if (isLoading) {
    return (
      <Container className='space-y-6'>
        <Row>
          <div className='flex items-center justify-between gap-6'>
            <div className='flex items-center gap-4'>
              <div className='animate-pulse'>
                <div className='h-10 w-10 bg-gray-20 rounded'></div>
              </div>
              <div className='animate-pulse'>
                <div className='h-8 bg-gray-20 rounded w-48 mb-2'></div>
                <div className='h-4 bg-gray-20 rounded w-32'></div>
              </div>
            </div>
            <div className='flex items-center gap-2'>
              <div className='animate-pulse'>
                <div className='h-10 w-20 bg-gray-20 rounded'></div>
              </div>
              <div className='animate-pulse'>
                <div className='h-10 w-24 bg-gray-20 rounded'></div>
              </div>
            </div>
          </div>
        </Row>

        <Row>
          <div className='grid grid-cols-1 lg:grid-cols-4 gap-8'>
            {/* Main Content */}
            <div className='lg:col-span-3 space-y-8'>
              {/* Metrics Loading */}
              <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                {Array.from({ length: 3 }).map((_, i) => (
                  <SkeletonMetric key={i} />
                ))}
              </div>

              {/* Tabs Loading */}
              <div className='space-y-6'>
                <div className='flex justify-center'>
                  <div className='animate-pulse'>
                    <div className='h-10 w-64 bg-gray-20 rounded'></div>
                  </div>
                </div>
                <SkeletonTable />
              </div>
            </div>

            {/* Product Details Sidebar Loading */}
            <div className='lg:col-span-1'>
              <Card className='p-6'>
                <div className='animate-pulse mb-6'>
                  <div className='h-6 bg-gray-20 rounded w-40'></div>
                </div>

                <div className='space-y-6'>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <SkeletonDetailItem key={i} />
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    );
  }

  return (
    <ViewGuard
      resource={RESOURCES.INVENTORY}
      fallback={<NoInventoryPermission onRequestAccess={() => {}} />}
    >
      <Container className='space-y-6'>
        <Row>
          <div className='flex items-center justify-between gap-6'>
            <BackButton
              text='Back to Inventory'
              title={inventory?.name}
              to='/inventories'
            />
            <div className='flex items-center gap-2'>
              <EditGuard resource={RESOURCES.INVENTORY}>
                <Button
                  color='gray'
                  hierarchy='outlined'
                  onClick={openInventoryUpdateSheet}
                  className='flex items-center gap-2'
                  disabled={isLoading}
                >
                  <PencilSimpleIcon size={16} />
                  Edit
                </Button>
              </EditGuard>

              {inventory?.approvalStatus === 'pending' && (
                <>
                  <ApproveGuard resource={RESOURCES.INVENTORY}>
                    <Button
                      color='destructive'
                      hierarchy='primary'
                      onClick={() => handleInventoryAction('decline')}
                      className='flex items-center gap-2'
                      disabled={actionLoading !== null}
                    >
                      {actionLoading === 'decline' ? (
                        <div className='animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full' />
                      ) : null}
                      Decline
                    </Button>
                  </ApproveGuard>
                  <ApproveGuard resource={RESOURCES.INVENTORY}>
                    <Button
                      color='brand'
                      hierarchy='primary'
                      onClick={() => handleInventoryAction('approve')}
                      className='flex items-center gap-2'
                      disabled={actionLoading !== null}
                    >
                      {actionLoading === 'approve' ? (
                        <div className='animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full' />
                      ) : null}
                      Approve
                    </Button>
                  </ApproveGuard>
                </>
              )}

              {(inventory?.approvalStatus === 'rejected' ||
                inventory?.approvalStatus === 'approved') && (
                <DeleteGuard resource={RESOURCES.INVENTORY}>
                  <Button
                    color='destructive'
                    hierarchy='primary'
                    onClick={() => handleInventoryAction('delete')}
                    className='flex items-center gap-2'
                    disabled={actionLoading !== null}
                  >
                    {actionLoading === 'delete' ? (
                      <div className='animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full' />
                    ) : null}
                    Delete Product
                  </Button>
                </DeleteGuard>
              )}
            </div>
          </div>
        </Row>

        <Row>
          <div className='grid grid-cols-1 lg:grid-cols-4 gap-8'>
            {/* Main Content */}
            <div className='lg:col-span-3 space-y-8'>
              {/* Metrics */}
              <MetricsDisplay metrics={metricsData} columns={3} />

              {/* Tabs using UI component */}
              <Tabs
                value={activeTab}
                onValueChange={value =>
                  setActiveTab(value as 'details' | 'pricing' | 'variants')
                }
              >
                <div className='flex justify-center'>
                  <TabsList>
                    <TabsTrigger value='details'>Product Details</TabsTrigger>
                    {(inventory as any)?.isVariant && (
                      <TabsTrigger value='pricing'>Pricing & Stock</TabsTrigger>
                    )}
                    {(inventory as any)?.isVariant && (
                      <TabsTrigger value='variants'>Variants</TabsTrigger>
                    )}
                  </TabsList>
                </div>

                {/* Product Details Tab */}
                <TabsContent value='details'>
                  <Card className='p-6'>
                    <h2 className='text-xl font-semibold mb-6'>
                      Product Information
                    </h2>
                    <div className='grid grid-cols-2 gap-6'>
                      <div>
                        <p className='text-sm text-gray-50 mb-1'>
                          Product Name
                        </p>
                        <p className='font-medium'>
                          {inventory?.name || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className='text-sm text-gray-50 mb-1'>SKU</p>
                        <p className='font-medium'>
                          {inventory?.sku || inventory?.inventoryId || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className='text-sm text-gray-50 mb-1'>Category</p>
                        <p className='font-medium'>
                          {capitalizeFirstLetter(
                            typeof inventory?.category === 'string'
                              ? (inventory?.category as any)
                              : (inventory as any)?.category?.primary || 'N/A'
                          )}
                        </p>
                      </div>
                      {!(inventory as any)?.isVariant && (
                        <div>
                          <p className='text-sm text-gray-50 mb-1'>Price</p>
                          <p className='font-medium'>
                            {formatCurrency(
                              (inventory as any)?.pricing?.sellingPrice ||
                                (inventory as any)?.price ||
                                0
                            )}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className='text-sm text-gray-50 mb-1'>
                          Stock Quantity
                        </p>
                        <p className='font-medium'>
                          {inventory?.isVariant
                            ? (inventory?.variant || []).reduce(
                                (sum: number, v: any) =>
                                  sum + (Number(v.quantity) || 0),
                                0
                              )
                            : inventory?.quantity || 0}
                        </p>
                      </div>
                      <div>
                        <p className='text-sm text-gray-50 mb-1'>Status</p>
                        <Badge
                          variant='outline'
                          style={{
                            backgroundColor: getStatusColor(
                              inventory?.approvalStatus || 'approved'
                            ),
                            color: getStatusTextColor(
                              inventory?.approvalStatus || 'approved'
                            ),
                            borderColor: getStatusColor(
                              inventory?.approvalStatus || 'approved'
                            ),
                          }}
                        >
                          {capitalizeFirstLetter(
                            inventory?.approvalStatus || 'approved'
                          )}
                        </Badge>
                      </div>
                      <div>
                        <p className='text-sm text-gray-50 mb-1'>
                          Measurement Unit
                        </p>
                        <p className='font-medium'>
                          {typeof inventory?.measurementUnit === 'string'
                            ? (inventory?.measurementUnit as any)
                            : (inventory as any)?.measurementUnit?.primary ||
                              'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className='text-sm text-gray-50 mb-1'>
                          Quality Grade
                        </p>
                        <p className='font-medium'>
                          {(inventory as any)?.quality?.grade || 'N/A'}
                        </p>
                      </div>
                      <div className='col-span-2'>
                        <p className='text-sm text-gray-50 mb-1'>Description</p>
                        <p className='text-gray-70'>
                          {inventory?.description || 'No description available'}
                        </p>
                      </div>
                    </div>
                  </Card>
                </TabsContent>

                {/* Pricing & Stock Tab (only for variant products) */}
                {(inventory as any)?.isVariant && (
                  <TabsContent value='pricing'>
                    <Card className='p-6'>
                      <h2 className='text-xl font-semibold mb-6'>
                        Pricing & Stock
                      </h2>
                      {/* Top-level pricing hidden for variant products as requested */}
                      <></>

                      {/* Valuation */}
                      <div className='mt-8'>
                        <h3 className='text-lg font-semibold mb-4'>
                          Valuation
                        </h3>
                        <div className='grid grid-cols-2 gap-6'>
                          <div>
                            <p className='text-sm text-gray-50 mb-1'>Method</p>
                            <p className='font-medium'>
                              {(inventory as any)?.valuation?.method || 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className='text-sm text-gray-50 mb-1'>
                              Unit Cost
                            </p>
                            <p className='font-medium'>
                              {formatCurrency(
                                (inventory as any)?.valuation?.unitCost || 0
                              )}
                            </p>
                          </div>
                          <div>
                            <p className='text-sm text-gray-50 mb-1'>
                              Total Value
                            </p>
                            <p className='font-medium'>
                              {formatCurrency(
                                (inventory as any)?.valuation?.totalValue || 0
                              )}
                            </p>
                          </div>
                          <div>
                            <p className='text-sm text-gray-50 mb-1'>
                              Last Calculated
                            </p>
                            <p className='font-medium'>
                              {(inventory as any)?.valuation?.lastCalculated
                                ? formatDate(
                                    (inventory as any).valuation.lastCalculated
                                  )
                                : 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </TabsContent>
                )}

                {/* Variants Tab */}
                {(inventory as any)?.isVariant && (
                  <TabsContent value='variants'>
                    <DataTable
                      data={variants}
                      columns={variantColumns}
                      header={{
                        title: 'Product Variants',
                        showSearch: true,
                        searchConfig: { placeholder: 'Search variants...' },
                      }}
                      emptyConfig={{
                        title: 'No Variants Found',
                        description: 'This product does not have any variants',
                        icon: (
                          <PackageIcon className='text-gray-40' size={16} />
                        ),
                      }}
                      enableSorting={true}
                      enablePagination={true}
                    />
                  </TabsContent>
                )}
              </Tabs>

              {/* Auto Reorder */}
              <Card className='p-6 mt-6'>
                <h2 className='text-xl font-semibold mb-6'>Auto Reorder</h2>
                <div className='grid grid-cols-2 gap-6'>
                  <div>
                    <p className='text-sm text-gray-50 mb-1'>Enabled</p>
                    <p className='font-medium'>
                      {(inventory as any)?.autoReorder?.enabled ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-50 mb-1'>Reorder Point</p>
                    <p className='font-medium'>
                      {(inventory as any)?.autoReorder?.reorderPoint ?? 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-50 mb-1'>
                      Reorder Quantity
                    </p>
                    <p className='font-medium'>
                      {(inventory as any)?.autoReorder?.reorderQuantity ??
                        'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-50 mb-1'>
                      Lead Time (days)
                    </p>
                    <p className='font-medium'>
                      {(inventory as any)?.autoReorder?.leadTimeDays ?? 'N/A'}
                    </p>
                  </div>
                </div>
              </Card>

              {/* Storage & Location */}
              <Card className='p-6 mt-6'>
                <h2 className='text-xl font-semibold mb-6'>
                  Storage & Location
                </h2>
                <div className='grid grid-cols-2 gap-6'>
                  <div>
                    <p className='text-sm text-gray-50 mb-1'>Conditions</p>
                    <p className='font-medium'>
                      {(inventory as any)?.storage?.conditions || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-50 mb-1'>Warehouse</p>
                    <p className='font-medium'>
                      {(inventory as any)?.storage?.location?.warehouse ||
                        'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-50 mb-1'>
                      Shelf Life (days)
                    </p>
                    <p className='font-medium'>
                      {(inventory as any)?.storage?.shelfLife?.days ?? 'N/A'}
                    </p>
                  </div>
                </div>
              </Card>

              {/* Compliance */}
              <Card className='p-6 mt-6'>
                <h2 className='text-xl font-semibold mb-6'>Compliance</h2>
                <div className='grid grid-cols-2 gap-6'>
                  <div>
                    <p className='text-sm text-gray-50 mb-1'>
                      Requires Certification
                    </p>
                    <p className='font-medium'>
                      {(inventory as any)?.compliance?.requiresCertification
                        ? 'Yes'
                        : 'No'}
                    </p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-50 mb-1'>Hazardous</p>
                    <p className='font-medium'>
                      {(inventory as any)?.compliance?.hazardousFlag
                        ? 'Yes'
                        : 'No'}
                    </p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-50 mb-1'>Restricted Sale</p>
                    <p className='font-medium'>
                      {(inventory as any)?.compliance?.restrictedSale
                        ? 'Yes'
                        : 'No'}
                    </p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-50 mb-1'>Export Eligible</p>
                    <p className='font-medium'>
                      {(inventory as any)?.compliance?.exportEligible
                        ? 'Yes'
                        : 'No'}
                    </p>
                  </div>
                  <div className='col-span-2'>
                    <p className='text-sm text-gray-50 mb-1'>
                      Regulatory Requirements
                    </p>
                    <p className='text-gray-70'>
                      {(
                        (inventory as any)?.compliance
                          ?.regulatoryRequirements || []
                      ).length > 0
                        ? (
                            (inventory as any)?.compliance
                              ?.regulatoryRequirements || []
                          ).join(', ')
                        : 'None'}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Product Details Sidebar */}
            <div className='lg:col-span-1'>
              <Card className='p-6'>
                <h2 className='text-xl font-semibold mb-6'>Product Details</h2>

                <div className='space-y-6'>
                  {/* Product ID */}
                  <div className='flex items-start gap-3'>
                    <div className='bg-success-5 p-2 rounded-full mt-1'>
                      <PackageIcon className='text-success-60' size={16} />
                    </div>
                    <div className='flex-1 min-w-0'>
                      <p className='text-sm text-gray-50 mb-1'>Product ID</p>
                      <p className='font-medium break-words'>
                        {inventory?._id}
                      </p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(inventory?._id || '')}
                      className='text-gray-40 hover:text-gray-60 p-1'
                    >
                      <CopyIcon size={14} />
                    </button>
                  </div>

                  {/* Created Date */}
                  <div className='flex items-start gap-3'>
                    <div className='bg-success-5 p-2 rounded-full mt-1'>
                      <CalendarIcon className='text-success-60' size={16} />
                    </div>
                    <div className='flex-1 min-w-0'>
                      <p className='text-sm text-gray-50 mb-1'>Created On</p>
                      <p className='font-medium'>
                        {inventory?.createdAt
                          ? formatDate(inventory.createdAt)
                          : 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Updated Date */}
                  {inventory?.updatedAt && (
                    <div className='flex items-start gap-3'>
                      <div className='bg-success-5 p-2 rounded-full mt-1'>
                        <CalendarIcon className='text-success-60' size={16} />
                      </div>
                      <div className='flex-1 min-w-0'>
                        <p className='text-sm text-gray-50 mb-1'>
                          Last Updated
                        </p>
                        <p className='font-medium'>
                          {formatDate(inventory.updatedAt)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </ViewGuard>
  );
};

export default InventoryShow;
